/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useState } from 'react';

import { CopyButton } from '@/primitives/CopyButton';

import './JsonTreeViewer.css';

type JsonValue = string | number | boolean | null | JsonObject | JsonArray;

interface JsonObject {
  [key: string]: JsonValue;
}

type JsonArray = JsonValue[];

type ValueType = 'null' | 'array' | 'object' | 'string' | 'number' | 'boolean';

export interface JsonTreeViewerProps {
  data: unknown;
  defaultExpanded?: boolean;
}

function getValueType(value: unknown): ValueType {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value as ValueType;
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function JsonPrimitive({ value, type }: { value: JsonValue; type: ValueType }) {
  let displayValue: string;
  let className: string;

  switch (type) {
    case 'string':
      displayValue = `"${escapeHtml(String(value))}"`;
      className = 'json-string';
      break;
    case 'number':
      displayValue = String(value);
      className = 'json-number';
      break;
    case 'boolean':
      displayValue = String(value);
      className = 'json-boolean';
      break;
    case 'null':
      displayValue = 'null';
      className = 'json-null';
      break;
    default:
      displayValue = String(value);
      className = '';
  }

  return <span className={className}>{displayValue}</span>;
}

export function JsonNode({
  itemKey,
  value,
  isLast,
  defaultExpanded = true,
}: {
  itemKey?: string;
  value: unknown;
  isLast: boolean;
  defaultExpanded?: boolean;
}) {
  const [isCollapsed, setIsCollapsed] = useState(!defaultExpanded);
  const type = getValueType(value);

  if (type === 'object' || type === 'array') {
    const isArray = type === 'array';
    const entries = isArray
      ? (value as JsonArray).map((v, i) => [String(i), v] as const)
      : Object.entries(value as JsonObject);
    const count = entries.length;
    const isEmpty = count === 0;

    return (
      <div className="json-item">
        <button
          type="button"
          className="json-toggle"
          onClick={() => !isEmpty && setIsCollapsed(!isCollapsed)}
          disabled={isEmpty}
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isCollapsed ? '▶' : '▼'}
        </button>

        {itemKey !== undefined && (
          <>
            <span className="json-key">&quot;{itemKey}&quot;</span>
            <span className="json-colon">: </span>
          </>
        )}

        <span className="json-bracket">{isArray ? '[' : '{'}</span>

        {isEmpty ? (
          <>
            <span className="json-bracket">{isArray ? ']' : '}'}</span>
            {!isLast && <span className="json-comma">,</span>}
          </>
        ) : (
          <>
            {isCollapsed && (
              <>
                <span className="json-preview">{isArray ? ` ${count} items ` : ` ${count} keys `}</span>
                <span className="json-bracket">{isArray ? ']' : '}'}</span>
                {!isLast && <span className="json-comma">,</span>}
              </>
            )}

            {!isCollapsed && (
              <>
                <div className="json-children">
                  {entries.map(([k, v], idx) => (
                    <JsonNode
                      key={k}
                      itemKey={isArray ? undefined : k}
                      value={v}
                      isLast={idx === entries.length - 1}
                      defaultExpanded={defaultExpanded}
                    />
                  ))}
                </div>
                <div className="json-item">
                  <span className="json-toggle-spacer"> </span>
                  <span className="json-bracket">{isArray ? ']' : '}'}</span>
                  {!isLast && <span className="json-comma">,</span>}
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  }

  // Primitive value
  return (
    <div className="json-item">
      <span className="json-toggle-spacer"> </span>

      {itemKey !== undefined && (
        <>
          <span className="json-key">&quot;{itemKey}&quot;</span>
          <span className="json-colon">: </span>
        </>
      )}

      <JsonPrimitive value={value as JsonValue} type={type} />

      {!isLast && <span className="json-comma">,</span>}
    </div>
  );
}

/**
 * Main JSON Tree Viewer Component
 */
export function JsonTreeViewer({ data, defaultExpanded = true }: JsonTreeViewerProps) {
  return (
    <div className="json-tree">
      <div className="json-tree-header">
        <CopyButton textToCopy={JSON.stringify(data, null, 2)} size="sm" />
      </div>
      <div className="json-tree-content">
        <JsonNode value={data} isLast={false} defaultExpanded={defaultExpanded} />
      </div>
    </div>
  );
}
